/*
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    ENGINEERING NOTES
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    1) Do not import any classes from the moment.js library (e.g. with
       import { moment } from 'moment'). This is becausethe moment.js classes can
       be very large and webpack has issues compressing them properly.
    2) Instead import the moment.js classes you need via HTML <script> tags.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import helper classes individually and not via the EzHelpers
    module. This is because the EzHelpers module imports all helper classes and can
    cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    IMPORTANT: Always import Enumeration classes individually and not via the
    EzEnumerations module. This is because the EzEnumerations module imports all
    enumeration classes and can cause circular dependencies and increase bundle size.
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following into this class to avoid circular
    dependencies:
        import { EzApi } from '/public/common/javascript/ezapi.js'
        import { EzUI } from '/public/common/javascript/ezui.js'
        import { ezUI } from '/public/common/javascript/ezui.js'
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
    WARNING: Do not import the following helper classes into this class to avoid
    circular dependencies:
        import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
        import { EzHtmlCharacterCode } from '/ezlibrary/helpers/EzHtmlCharacterCode.js';
        import { EzConsole } from '/ezlibrary/helpers/EzConsole.js';
        import { EzDateTime } from '/public/javascript/common/ez-date-time.js';
        import { EzDateTime } from '/ezlibrary/helpers/EzDateTime.js';
    ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
*/

import {
    decode,
    encode
} from 'html-entities';

import {
    EzException,
    EzBadParamException
} from '/ezlibrary/exceptions/EzExceptions.js';

import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
import { EzObject } from '/ezlibrary/helpers/EzObject.js';
import { EzBoolean } from '/ezlibrary/helpers/EzBoolean.js';
import { EzNumber } from '/ezlibrary/helpers/EzNumber.js';
import { EzString } from '/ezlibrary/helpers/EzString.js';
import { EzArray } from '/ezlibrary/helpers/EzArray.js';
import { EzFunction } from '/ezlibrary/helpers/EzFunction.js';
import { EzJson } from '/ezlibrary/helpers/EzJson.js';
import { EzRegEx } from '/ezlibrary/helpers/EzRegEx.js';

/**
 * @class
 * @extends {EzStaticClass}
 * @description
 * Static class that provides utility methods and properties for generating HTML
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 * Import with:
 *     import { EzHtml } from '/ezlibrary/helpers/EzHtml.js';
 * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
 */
export class EzHtml extends EzStaticClass {
    /**
     * @static
     * @public @readonly @property
     * Gets the @swc/html default options to use when minifying HTML fragments
     *
     * @returns {object}
     */
    static get ezDefaultSwcHtmlMinifyOptions() {
        return {
            scriptingEnabled: true,
            forceSetHtml5Doctype: true,
            collapseWhitespaces: 'smart',
            removeEmptyMetadataElements: true,
            removeComments: false,
            minifyConditionalComments: false,
            removeEmptyAttributes: false,
            removeRedundantAttributes: 'none',
            collapseBooleanAttributes: true,
            normalizeAttributes: true,
            minifyJson: true,
            minifyJs: true,
            minifyCss: true,
            minifyAdditionalScriptsContent: true,
            minifyAdditionalAttributes: true,
            sortSpaceSeparatedAttributeValues: false,
            sortAttributes: false,
            tagOmission: false,
            selfClosingVoidElements: true,
            quotes: true
        };
    }

    /**
     * @static
     * @private @field
     * Stores a key = value map of HTML property name to property name string
     * @returns {object}
     */
    static #htmlPropName = {
        id: 'id',
        href: 'href',
        src: 'src',
        style: 'style',
        class: 'class',
        rel: 'rel',
        name: 'name',
        charset: 'charset',
        content: 'content',
        type: 'type',
        sizes: 'sizes',
        color: 'color',
        lang: 'lang',
        alt: 'alt'
    };
    /**
     * @public @readonly @property
     * Gets a key = value map of HTML property name to property name string
     * @returns {object}
     */
    static get htmlPropName() {
        return this.#htmlPropName;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the HTML property name 'href'
     * @returns {string}
     */
    static htmlProp_href() {
        return 'href';
    }

    /**
     * @private @field
     * Stores a DomParser instance for use in validating HTML strings
     * @type {DomParser}
     */
    static #domParser = null;
    /**
     * @static
     * @public @readonly @property
     * Gets the DomParser instance for use in validating HTML strings
     * @returns {DomParser}
     */
    static get domParser() {
        if (null == this.#domParser) {
            this.#domParser = new DOMParser();
        }

        return this.#domParser;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant string of for browser code html/text content type: 'text/html'
     *
     * @returns {string}
     */
    static get ezBrowserCodeHtmlTextContentType() {
        return 'text/html';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets constant string of defaultHtml: '<html><body></body></html>'
     *
     * @returns {string}
     */
    static get ezDefaultHtml() {
        return '<html><body></body></html>';
    }

    /**
     * @static
     * @private @method
     * Processes a the template strings aStrings value for the provided index.
     *
     * @param {undefined|null|array} templateStrings
     * @param {number} index
     */
    static #ezProcessTemplateStringValue(templateStrings, index) {
        if (!EzArray.isArray(templateStrings)) {
            throw new EzBadParamException(
                'templateStrings',
                this,
                this.#ezProcessTemplateStringValue);
        }
        if (!EzNumber.isNumber(index) || 0 > index) {
            throw new EzBadParamException(
                'index',
                this,
                this.#ezProcessTemplateStringValue);
        }

        if (index >= templateStrings.length) {
            return EzString.EMPTY;
        }

        let templateStringValue = EzObject.assignOrDefault(
            templateStrings[index],
            EzString.EMPTY);

        if (!EzString.isString(templateStringValue)) {
            templateStringValue = EzString.stringOrDeafult(
                templateStringValue?.toString(),
                templateStringValue);
        }

        return templateStringValue
            .replace(
                EzRegEx.MATCH_2_OR_MORE_SPACES,
                ' ')
            .replace(
                EzRegEx.MATCH_ALL_TABS,
                ' ');
    }

    /**
     * @static
     * @private @method
     * Processes a template string's keys value for the provided index.
     *
     * @param {undefined|null|object} templateKeys
     * @param {number} index
     */
    static #ezProcessTemplateKey(templateKeys, index) {
        if (!EzNumber.isNumber(index) || 0 > index) {
            throw new EzBadParamException(
                'index',
                this,
                this.#ezProcessTemplateKey);
        }

        if (!EzObject.isValid(templateKeys?.[index])) {
            return EzString.EMPTY;
        }

        const templateKey = EzObject.assignOrDefault(
            templateKeys[index],
            EzString.EMPTY);

        return !EzString.isString(templateKey)
            ? EzString.stringOrDefault(
                templateKey?.toString(),
                templateKey)
            : templateKey;
    }

    /**
     * @static
     * @public @method
     * Javascript Template String Method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
     * the value with one space in front, once space at end and a line feed.
     *~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Ouput format: ' ${value_with_extra_space_removed} \n';
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example use:
     *      EzHTML.build`
     *          <h1
     *              class="ezH1">
     *              Hello!
     *          </h1>`;
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {array} aStrings
     * @param {array} aKeys
     * @returns {string}
     * @deprecated
     * Migrate to: EzHtml.build`...`
     */
    static template = EzString.build;

    /**
     * @static
     * @public @method
     * Javascript Template String Method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
     * the value with one space in front, once space at end and a line feed.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Ouput format: ' ${value_with_extra_space_removed} \n';
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example use:
     *      EzHTML.build`
     *          <h1
     *              class="ezH1">
     *              Hello!
     *          </h1>`;
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {array} templateStrings
     * @param {...*} templateKeys
     *
     * @returns {string}
     */
    static build(templateStrings, ...templateKeys) {
        /*const debugLog = EzFunction.functionOrDefault(
            EzObject.isValid(globalThis?.ezApi?.ezclocker?.ezLogger)
                ? globalThis.ezApi.ezclocker.ezLogger.debug
                : globalThis?.console?.debug,
            () => {  ignoring debug  });*/

        let browserCode = EzString.EMPTY;

        if (!EzArray.hasLength(templateStrings)) {
            return `${browserCode}`;
        }

        for (let index = 0;index < templateStrings.length;index++) {
            const templateStringValue = this.#ezProcessTemplateStringValue(templateStrings, index);

            const templateKeyValue = this.#ezProcessTemplateKey(templateKeys, index);

            browserCode = `${browserCode}${templateStringValue}${templateKeyValue}`;

            /*debugLog(
                EzString.build`
                    [Processed Template String Value: ${templateStringValue}]
                    [Processed Template Key Value: ${templateKeyValue}]
                    [Updated browser code string: ${browserCode}]`);*/
        }

        //debugLog(`Final browser code string: ${browserCode}`);

        // return `${minifySync(`${browserCode}`, EzHtml.ezDefaultSwcHtmlMinifyOptions)}`;
        return `${browserCode}`;
    }

    /**
     * @static
     * @public @method
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * ENGINEERING NOTES
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Use EzHtml.build`...` if the HTML you are building is not yet parseable.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     *
     * Javascript Template String Method
     * Removes all extract space from the provided aTemplateLitteralValue param. Then returns
     * the value with one space in front, once space at end and a line feed.
     *
     * Will pass the result to EzHtml.validateHtmlString(...) to check for HTML
     * parsing errors before returning. Any detected parsing errors will get logged
     * into the console.
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Ouput format: ' ${value_with_extra_space_removed} \n';
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * Example use:
     *      EzHTML.build`
     *          <h1>
     *              Hello!
     *          </h1>`;
     * ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
     * @param {array} aStrings
     * @param {array} aKeys
     * @returns {string}
     */
    static buildValid(templateStrings, ...templateKeys) {
        const browserCode = EzHtml.build(templateStrings, ...templateKeys);

        const validateBrowserCodeResponse = EzHtml.validateBrowserCodeString(
            browserCode,
            'text/html',
            true,
            true);

        return `${validateBrowserCodeResponse.browserCode}`;
    }

    /**
     * @static
     * @public @method
     * Validates htmlString of browser code with content type text/html for parsing errors.
     *
     * @param {string} htmlString
     * Default: EzHtml.ezDefaultHtml
     * @param {boolean} logParserError
     * Default: false
     * @param {boolean} throwOnParserError
     * Default: false
     *
     * @returns {object}
     *  {
     *      errorCode: {0 = no error, 1 = error},
     *      message: {`Valid ${ contentType } ` | `Not Valid ${ contentType } `},
     *      parseErrors: { null | PaserError Object},
     *      browserCode: {browserCode | EzHtml.ezDefaultHtml};
     *  };
     */
    static validateHtmlString(htmlString = EzHtml.ezDefaultHtml, logParserError = false, throwOnParserError = false) {
        return EzHtml.validateBrowserCodeString(
            EzString.stringWithLengthOrDefault(
                htmlString,
                EzHtml.ezDefaultHtml),
            EzHtml.ezBrowserCodeHtmlTextContentType,
            logParserError,
            throwOnParserError);
    }

    /**
     * @static
     * @public @method
     * Validates browserCode string of browser code using the provided contentType.
     *
     * @param {string} browserCode
     * Default: EzHtml.ezDefaultHtml
     * @param {string} contentType
     * Default: 'text/html'}
     * @param {boolean} logParserError
     * Default: false
     * @param {boolean} throwOnParseError
     * Default: false
     *
     * @returns {object}
     *  {
     *      errorCode: {0 = no error, 1 = error},
     *      message: {`Valid ${ contentType } ` | `Not Valid ${ contentType } `},
     *      parseErrors: { null | PaserError Object},
     *      browserCode: {browserCode | EzHtml.ezDefaultHtml};
     *  };
     */
    static validateBrowserCodeString(browserCode = EzHtml.ezDefaultHtml, contentType = EzHtml.ezBrowserCodeHtmlTextContentType, logParserError = false,
        throwOnParserError = false) {
        if (!EzString.hasText(browserCode)) {
            browserCode = EzHtml.ezDefaultHtml;
        }

        if (!EzString.hasText(contentType)) {
            contentType = EzHtml.ezBrowserCodeHtmlTextContentType;
        }

        const validateBrowserCodeResponse = {
            errorCode: 0,
            message: `Valid ${contentType} `,
            parseErrors: null,
            browserCode
        };

        if (!EzString.hasText(browserCode)) {
            return validateBrowserCodeResponse;
        }

        const htmlDocument = EzHtml.domParser.parseFromString(
            browserCode,
            contentType);

        const parseErrors = htmlDocument.documentElement.querySelector('parsererror');

        if (EzObject.isValid(parseErrors)) {
            validateBrowserCodeResponse.errorCode = 1;

            validateBrowserCodeResponse.message = `Not Valid ${contentType} `;

            validateBrowserCodeResponse.parseErrors = parseErrors;

            const ezException = new EzException(
                EzString.em`
                    EzClocker browser code validation failure for content type ${contentType}:
                    ${EzJson.toJson(validateBrowserCodeResponse)} `);

            if (EzBoolean.booleanOrFalse(logParserError)) {
                globalThis.console.error(
                    EzString.em`
                        ${ezException.message}
                        [Stack trace: ${ezException.stack}]`);
            }

            if (EzBoolean.booleanOrFalse(throwOnParserError)) {
                throw ezException;
            }
        }

        return validateBrowserCodeResponse;
    }

    /**
     * @static
     * @public @method
     * Removes all spaces and line feeds from the provided aTemplateLitteralValue param. Then returns
     * the trimmed value.
     * Result template: '${value_with_no_space_linefeeds}';
     * @param {string} aTemplateLitteralValue
     * @returns {string}
     */
    static buildId(aStrings, ...aKeys) {
        let cleanValues = EzString.EMPTY;

        if (EzArray.arrayHasLength(aStrings)) {
            for (let x = 0;x < aStrings.length;x++) {
                let aString = aStrings[x];

                aString = EzString.trimOrEmpty(
                    !EzString.isString(aString)
                        ? EzObject.assignOrDefault(aString, EzString.EMPTY).toString()
                        : aString);

                let aKey = x < aKeys.length
                    ? aKeys[x]
                    : EzString.EMPTY;

                aKey = EzString.stringOrEmpty(
                    !EzString.isString(aKey)
                        ?
                        EzObject.assignOrDefault(
                            aKey,
                            EzString.EMPTY)
                            .toString()
                        : aKey);

                const cleanLine = `${aString.trimStart().replace(EzRegEx.MATCH_ALL_WHITESPACE_CHARACTERS, '')}${aKey} `.trim();

                cleanValues = `${cleanValues}${cleanLine} `.trim();
            }
        }

        return `${cleanValues} `.trim();
    }

    /**
     * @public @readonly @property
     * Gets an open-ended starting div tag: '<div ';
     * @returns {string}
     */
    static get divStart() {
        return '<div ';
    }

    /**
     * @public @readonly @property
     * Gets the ending div tag: '</div>'
     * @returns {string}
     */
    static get divEnd() {
        return '</div>';
    }

    /**
     * @static
     * @public @method
     * Builds an HTML a element
     * @param {string} id
     * The id is required
     * @param {undefined|null|array} additionalElementAttributes
     * Array of additional attributes to add to the HTML element
     * @param {undefined|null|string} innerHtml
     * HTML/Text to place between the open and close elements.
     */
    static div(id = null, additionalElementAttributes = null, innerHtml = EzString.EMPTY) {
        if (!EzString.hasText(id)) {
            throw new EzBadParamException(
                'id',
                EzHtml,
                EzHtml.a);
        }

        const additionalElementAttributesHTML = EzArray.hasLength(additionalElementAttributes)
            ? EzHtml.attributeArrayToElementAttributes(additionalElementAttributes)
            : EzString.empty;

        return EzString.hasText(additionalElementAttributesHTML)
            ? `${EzHtml.divStart} id = "${id}" ${additionalElementAttributesHTML}> ${EzString.trimOrEmpty(innerHtml)}${EzHtml.divEnd} `
            : `${EzHtml.divStart} id = "${id}" > ${EzString.trimOrEmpty(innerHtml)}${EzHtml.divEnd} `;
    }

    /**
     * @public @readonly @property
     * Gets an open ended starting a (anchor) tag: '<a '
     * @returns {string}
     */
    static get aStart() {
        return '<a ';
    }

    /**
     * @public @readonly @property
     * Gets the closing a (anchor) tag: '</a>'
     * @returns {string}
     */
    static get aEnd() {
        return '</a>';
    }

    /**
     * @static
     * @public @method
     * Builds an HTML a element
     * @param {string} id
     * REQUIRED: HTML element Id for the <a/> tag
     * @param {undefined|null|string} href
     * REQUIRED: default is '#'
     * @param {undefined|null|array} additionalElementAttributes
     * Optional: Array of additional attributes to add to the HTML element
     * @param {string} innerHtml
     * Optional: HTML/Text to place between the open and close elements.
     * If not provided, the HREF value is used instead.
     */
    static a(id, href = '#', additionalElementAttributes = null, innerHtml = null) {
        if (!EzString.hasText(id)) {
            throw new EzBadParamException(
                'id',
                EzHtml,
                EzHtml.a);
        }

        href = EzString.trimOrEmpty(href);

        innerHtml = EzString.trimOrEmpty(innerHtml);

        if (!EzString.hasText(href)) {
            href = '#';
        }

        if (!EzString.hasText(innerHtml)) {
            if ('#' === href) {
                throw new EzBadParamException(
                    'innerHtml',
                    EzHtml,
                    EzHtml.a);
            }

            innerHtml = href;
        }

        const additionalElementAttributesHTML = EzArray.hasLength(additionalElementAttributes)
            ? EzHtml.attributeArrayToElementAttributes(additionalElementAttributes)
            : EzString.empty;

        return EzString.hasText(additionalElementAttributesHTML)
            ? `${EzHtml.aStart} id = "${id}" href = "${href}" ${additionalElementAttributesHTML}> ${innerHtml}${EzHtml.aEnd} `
            : `${EzHtml.aStart} id = "${id}" href = "${href}" > ${innerHtml}${EzHtml.aEnd} `;
    }

    /**
     * @static
     * @public @readonly @property
     * Gets a open-ended button element starting tag: '<button '
     * @returns {string}
     */
    static get buttonStart() {
        return '<button ';
    }

    /**
     * @static
     * @public @readonly @property
     * Gets the button element's ending tag: '</button>'
     * @returns {string}
     */
    static get buttonEnd() {
        return '</button>';
    }

    /**
     * @static
     * @public @method
     * Returns the HTML for a button.
     * Template:
     *     EzHtml.template`
    * <button id="${id}" class="${classes}" title="${title}" onclick="${onclick}" ${optionalAttributes[0]} ...${optionalAttributes[N]}>
        *             ${content}
        *         </button>`
     * @param {undefined|null|string} id
     * Required
     * A valid id is required to create the <button> html.
     * @param {undefined|null|string} classes
     * Optional class attribute value.
     * If undefined, null, or empty string the attribute is not added to the <button> tag.
     * @param {undefined|null|string} title
     * Default: ''
     * Optional title attribute value.
     * If undefined, null, or empty string the attribute is not added to the <button> tag.
     * @param {undefined|null|string} onclick
     * Default: ''
     * This is not an event handler!
     * If undefined, null, or empty string the attribute is not added to the <button> tag.
     * @param {undefined|null|array} additionalElementAttributes
     * An array of additional attributes to add to the <button> tag.
     * Example: [
     *     'style="display:none",
     *     "disabled"'
     * ]
     * Default: null
     * @param {undefined|null|string} content
     * Optional content for the button.
     * @returns {string}
     */
    static button(id = null, classes = null, title = null, onclick = null, additionalElementAttributes = null, innerHtml = null) {
        if (!EzString.isString(id)) {
            throw new EzBadParamException(
                'id',
                EzHtml,
                EzHtml.button);
        }

        classes = EzString.trimOrEmpty(classes);

        title = EzString.trimOrEmpty(title);

        onclick = EzString.trimOrEmpty(onclick);

        innerHtml = EzString.trimOrEmpty(innerHtml);

        const classAttributeHTML = EzString.hasText(classes)
            ? ` class="${classes}"`
            : EzString.EMPTY;

        const titleAttributeHTML = EzString.hasText(title)
            ? ` title = "${title}"`
            : EzString.EMPTY;

        const onClickAttribuetHtml = EzString.hasText(onclick)
            ? ` onclick = "${onclick}"`
            : EzString.EMPTY;

        const additionalElementAttributesHTML = EzArray.hasLength(additionalElementAttributes)
            ? ` ${EzHtml.attributeArrayToElementAttributes(additionalElementAttributes)} `
            : EzString.EMPTY;

        const elementAttributes = `id = "${id}"${classAttributeHTML}${titleAttributeHTML}${onClickAttribuetHtml}${additionalElementAttributesHTML} `;

        return `${EzHtml.buttonStart}${elementAttributes}> ${innerHtml}${EzHtml.buttonEnd} `;
    }

    /**
     * @static
     * @public @method
     * Transforms an array of HTML element attributes to a string of attributes that
     * can then get added to an HTML template line.
     * Example:
     * attributesList = [
     *     `class="${MyClassName}"',
    * 'onclick="ezApi.ezclocker.ezApi.ezToJson(aObject)"'
    * ]
     *
     * Will transform to:
     * 'class="${MyClassName}" onclick="ezApi.ezclocker.ezApi.ezToJson(aObject)"'
    * @param { array } attributes
        * @returns { string }
     * /
    static attributeArrayToElementAttributes(elementAttributes) {
    let elementAttributesHTML = EzString.EMPTY;

    if (EzArray.hasLength(elementAttributes)) {
        for (const attributeHTML of elementAttributes) {
            if (EzString.hasText(attributeHTML)) {
                elementAttributesHTML = `${elementAttributesHTML} ${attributeHTML}`.trim();
            }
        }
    }

    return elementAttributesHTML;
}

    /**
     * @static
     * @public @method
     * Replaces HTML special character with their HTML codes
     * Convenience method that forwards to EzString.simpleEncodeHtml
     * @param {string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlEncode(...)
     */
    static simpleEncodeHtml(value) {
        return EzHtml.htmlEncode(value);
    }

    /**
     * @static
     * @public @method
     * Encodes html characters in the provided value.
     * Convenience method that forwards to EzString.encodeHtml
     * @param {string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlEncode(...)
     */
    static encodeHtml(value) {
        return EzHtml.htmlEncode(value);
    }

    /**
     * @static
     * @public @method
     * Decodes html characters in the provided value.
     * Convenience method that forwards to EzString.decodeHtml
     * @param {string} value
     * @returns {string}
     * @deprecated
     * Migrate to: EzString.htmlDecode(...)
     */
    static decodeHtml(value) {
        return EzHtml.htmlDecode(value);
    }

    /**
     * @static
     * @public @method
     * Encodes the provided aValue as HTML.
     * Example:
     *  EzChars.htmlEncode('< > " \' & © ∆')
     *  Result: '&lt; &gt; &quot; &apos; &amp; © ∆'
     * @param {string} rawString
     * @param {undefined|null|object} encodingOptions
     * Default:
     *     {
     *      level: 'all',
     *      mode: 'specialChars'
     *      numeric: 'decimal'
     *  }
     * @returns {string}
     * Returns an HTML encoded string
     */
    static htmlEncode(rawString, encodingOptions) {
        if (!EzString.hasText(rawString)) {
            return EzString.EMPTY;
        }

        if (!EzObject.isValid(encodingOptions)) {
            encodingOptions = EzString.buildEncodingOptions(
                'all',
                'specialChars',
                'decimal');
        }

        if (0 === rawString?.length) {
            return rawString;
        }

        return encode(
            rawString,
            encodingOptions);
    }

    /**
     * @static
     * @public @method
     * Decodes the provided htmlEncodedValue to a raw string value.
     * Example:
     *  EzChars.htmlDecode('&lt; &gt; &quot; &apos; &amp; &#169; &#8710;')
     *  Result: '< > " \' & © ∆'
     * @param {string} htmlEncodedValue
     * @param {undefined|null|object} decodingOptions
     * Default:
     *  {
     *      level: 'all',
     *      scope: 'body'
     *  }
     * @returns {string}
     * Returns an HTML encoded string
     */
    static htmlDecode(htmlEncodedValue, decodingOptions) {
        if (!EzString.hasText(htmlEncodedValue)) {
            return EzString.EMPTY;
        }
        if (!EzObject.isValid(decodingOptions)) {
            decodingOptions = EzString.buildDecodingOptions('all', 'body');
        }

        if (0 === htmlEncodedValue?.length) {
            return htmlEncodedValue;
        }

        return decode(
            htmlEncodedValue,
            decodingOptions);
    }

    /**
     * @static
     * @public @function
     * Builds an HTML anchor element with the href set to the support email and subject line if provided.
     * Template: `<a id="${aId}" href="mailto:support@ezclocker.com?subject='${subject}`">support@ezclocker.com</a>`
     * @param {undefined|null|string} subject
     * Default: EzString.EMPTY
     * @param {undefined|null|string} aId
     * Default: 'EzContactSupportLink'
     * @returns {string}
     */
    static buildEmailSupportLink(subject = EzString.EMPTY, aId = 'EzContactSupportLink') {
        const linkHref = EzString.hasText(subject)
            ? `mailto:support@ezclocker.com?subject='${EzUrl.encodeUrl(subject)}'`
            : `mailto:support@ezclocker.com`;

        const linkId = EzString.stringWithTextOrDefault(
            aId,
            'EzContactSupportLink');

        return EzHtml.build`
            <a
                id="${linkId}"
                href="${linkHref}">
                support@ezclocker.com
            </a>`;
    }
}
